import {
  PeppolProviderError,
  type DownloadArtefactInput,
  type DownloadArtefactResult,
  type ListTransactionsQuery,
  type ListTransactionsResult,
  type PeppolTransactionProvider,
  type ProviderConnectivity,
  type ProviderRegion,
  type SendDocumentInput,
  type SendDocumentResult,
  type Transaction,
} from '@solara/peppol-provider';

interface MockTransactionState {
  /** Internal store of transactions, keyed by `transactionId`. */
  store: Map<string, Transaction>;
  /** Auto-incrementing counter for transaction IDs. */
  counter: number;
}

/**
 * In-memory mock of TickStar Transaction API.
 *
 * Used by the customer portal and admin console while the real TickStar trial
 * account is being provisioned. Renders deterministic data and forwards a
 * `showDemoBadge: true` connectivity flag so the UI can surface a "DEMO ·
 * mock TickStar" pill — important because every invoice you see in the demo
 * is fake.
 *
 * Behaviour:
 * - `sendDocument` enqueues a PROCESSING transaction and transitions it to
 *   COMPLETED after ~1.2 seconds (configurable).
 * - `acknowledgeTransaction` moves a COMPLETED transaction to ACKNOWLEDGED.
 * - `listTransactions` filters by direction / messageIdentifier / time window.
 */
export class MockTickstarTransactionProvider implements PeppolTransactionProvider {
  private readonly state: MockTransactionState;

  constructor(
    private readonly region: ProviderRegion = 'EU',
    /** Milliseconds before PROCESSING → COMPLETED transition. */
    private readonly autoCompleteDelayMs = 1200,
    seedTransactions: Transaction[] = [],
  ) {
    this.state = { store: new Map(), counter: seedTransactions.length };
    for (const tx of seedTransactions) this.state.store.set(tx.transactionId, tx);
  }

  connectivity(): ProviderConnectivity {
    return {
      region: this.region,
      mode: 'mock',
      label: `TickStar ${this.region} · MOCK`,
      showDemoBadge: true,
    };
  }

  async ping(): Promise<boolean> {
    return true;
  }

  async sendDocument(input: SendDocumentInput): Promise<SendDocumentResult> {
    if (!input.payload || input.payload.length < 100) {
      throw new PeppolProviderError('SBDH_INVALID', 'Mock provider rejected: payload too short');
    }
    const counter = ++this.state.counter;
    const now = new Date();
    const messageIdentifier = extractMessageIdentifier(input.payload) ?? cryptoUuid();
    const transactionId = `tx-mock-${String(counter).padStart(8, '0')}`;
    const tx: Transaction = {
      transactionId,
      messageIdentifier,
      direction: 'OUTBOUND',
      state: 'PROCESSING',
      documentType: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
      senderParticipantId: extractSender(input.payload) ?? 'iso6523-actorid-upis::0230:UAE-TRN-MOCK',
      receiverParticipantId:
        extractReceiver(input.payload) ?? 'iso6523-actorid-upis::0230:UAE-TRN-RECIPIENT',
      createdAt: now.toISOString(),
      artefacts: [
        {
          artefactId: `art-${transactionId}-outbound-source`,
          type: 'OUTBOUND_SOURCE',
          contentType: 'application/xml',
          sizeBytes: input.payload.length,
        },
      ],
    };
    this.state.store.set(transactionId, tx);
    // Schedule the COMPLETED transition.
    setTimeout(() => {
      const current = this.state.store.get(transactionId);
      if (!current || current.state !== 'PROCESSING') return;
      this.state.store.set(transactionId, {
        ...current,
        state: 'COMPLETED',
        status: 'SUCCESSFUL',
        completedAt: new Date().toISOString(),
        artefacts: [
          ...current.artefacts,
          {
            artefactId: `art-${transactionId}-as4-receipt`,
            type: 'AS4_RECEIPT',
            contentType: 'application/xml',
            sizeBytes: 1284,
          },
        ],
      });
    }, this.autoCompleteDelayMs);
    return {
      transactionId,
      messageIdentifier,
      acceptedAt: now.toISOString(),
    };
  }

  async getTransaction(transactionId: string): Promise<Transaction> {
    const tx = this.state.store.get(transactionId);
    if (!tx) {
      throw new PeppolProviderError(
        'TRANSACTION_NOT_FOUND',
        `Mock provider: no transaction with id ${transactionId}`,
      );
    }
    return tx;
  }

  async listTransactions(query: ListTransactionsQuery): Promise<ListTransactionsResult> {
    let items = Array.from(this.state.store.values());
    if (query.direction) items = items.filter((t) => t.direction === query.direction);
    if (query.messageIdentifier)
      items = items.filter((t) => t.messageIdentifier === query.messageIdentifier);
    if (query.createdSince)
      items = items.filter((t) => t.createdAt >= query.createdSince!);
    if (query.createdUntil)
      items = items.filter((t) => t.createdAt < query.createdUntil!);
    if (!query.includeAcknowledged)
      items = items.filter((t) => t.state !== 'ACKNOWLEDGED' && t.state !== 'PURGED');
    items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    const limit = query.limit ?? 50;
    const sliced = items.slice(0, limit);
    return {
      items: sliced,
      totalCount: items.length,
      nextCursor: items.length > limit ? `cursor-${limit}` : undefined,
    };
  }

  async downloadArtefact(input: DownloadArtefactInput): Promise<DownloadArtefactResult> {
    const tx = this.state.store.get(input.transactionId);
    const artefact = tx?.artefacts.find((a) => a.artefactId === input.artefactId);
    if (!tx || !artefact) {
      throw new PeppolProviderError(
        'TRANSACTION_NOT_FOUND',
        `Mock provider: no artefact ${input.artefactId} on transaction ${input.transactionId}`,
      );
    }
    const samplePayload = `<?xml version="1.0" encoding="UTF-8"?><!-- DEMO ARTEFACT — mock TickStar provider, not a real Peppol payload -->\n<Artefact id="${artefact.artefactId}" type="${artefact.type}"/>`;
    return {
      artefactId: artefact.artefactId,
      contentType: artefact.contentType,
      bodyBase64:
        typeof btoa === 'function'
          ? btoa(samplePayload)
          : Buffer.from(samplePayload, 'utf8').toString('base64'),
    };
  }

  async acknowledgeTransaction(transactionId: string): Promise<void> {
    const tx = this.state.store.get(transactionId);
    if (!tx) {
      throw new PeppolProviderError(
        'TRANSACTION_NOT_FOUND',
        `Mock provider: no transaction with id ${transactionId}`,
      );
    }
    if (tx.state === 'ACKNOWLEDGED') {
      throw new PeppolProviderError('ALREADY_ACKNOWLEDGED', 'Mock: already acknowledged');
    }
    this.state.store.set(transactionId, {
      ...tx,
      state: 'ACKNOWLEDGED',
      acknowledgedAt: new Date().toISOString(),
    });
  }

  /**
   * Test helper — direct insert of a transaction into the mock store so the
   * admin console can preview INBOUND / FAILED states without going through
   * `sendDocument`.
   */
  injectTransaction(tx: Transaction): void {
    this.state.store.set(tx.transactionId, tx);
  }
}

// --- Helpers -------------------------------------------------------

function extractMessageIdentifier(payload: string): string | undefined {
  const m = payload.match(/<InstanceIdentifier>\s*([^<\s]+)\s*<\/InstanceIdentifier>/);
  return m?.[1];
}
function extractSender(payload: string): string | undefined {
  const m = payload.match(/<Sender>[\s\S]*?<Identifier[^>]*>\s*([^<\s]+)\s*<\/Identifier>/);
  return m?.[1] ? `iso6523-actorid-upis::${m[1]}` : undefined;
}
function extractReceiver(payload: string): string | undefined {
  const m = payload.match(/<Receiver>[\s\S]*?<Identifier[^>]*>\s*([^<\s]+)\s*<\/Identifier>/);
  return m?.[1] ? `iso6523-actorid-upis::${m[1]}` : undefined;
}

function cryptoUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for very old runtimes — not crypto-secure but fine for mock IDs.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
